import { useState, useEffect, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { PaneContainer } from "./PaneContainer";
import { DateRange } from "./DateRangeFilter";
import { ClinicalMeasurement } from "../../../../types";

export interface Tab {
  id: string;
  measureName: string;
}

interface Pane {
  id: string;
  tabs: Tab[];
  activeTabId: string;
}

interface PaneManagerProps {
  tabs: Tab[];
  activeTabId: string;
  onTabsUpdate: (tabs: Tab[], activeTabId: string) => void;
  globalDateRange: DateRange;
  measureDataMap: Record<string, ClinicalMeasurement>;
}

export function PaneManager({
  tabs,
  activeTabId,
  onTabsUpdate,
  globalDateRange,
  measureDataMap,
}: PaneManagerProps) {
  const isInternalUpdate = useRef(false);
  const [panes, setPanes] = useState<Pane[]>(() => {
    if (tabs.length > 0) {
      return [
        {
          id: `pane-${Date.now()}`,
          tabs: tabs,
          activeTabId: activeTabId,
        },
      ];
    }
    return [];
  });

  // Initialize panes when tabs change from parent
  useEffect(() => {
    if (tabs.length > 0) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setPanes((prevPanes) => {
        if (prevPanes.length === 0) {
          return [
            {
              id: `pane-${Date.now()}`,
              tabs: tabs,
              activeTabId: activeTabId,
            },
          ];
        } else {
          const allExistingTabIds = new Set(
            prevPanes.flatMap((p) => p.tabs.map((t) => t.id)),
          );
          const newTabs = tabs.filter((t) => !allExistingTabIds.has(t.id));

          if (newTabs.length > 0) {
            const newPanes = [...prevPanes];
            newPanes[0] = {
              ...newPanes[0],
              tabs: [...newPanes[0].tabs, ...newTabs],
              activeTabId: newTabs[newTabs.length - 1].id,
            };
            return newPanes;
          }

          const activePane = prevPanes.find((p) =>
            p.tabs.some((t) => t.id === activeTabId),
          );
          if (activePane && activePane.activeTabId !== activeTabId) {
            return prevPanes.map((pane) =>
              pane.id === activePane.id
                ? { ...pane, activeTabId: activeTabId }
                : pane,
            );
          }

          return prevPanes;
        }
      });
    } else if (tabs.length === 0) {
      setPanes([]);
    }
  }, [tabs, activeTabId]); // Dependencies are fine here since setPanes is stable

  // Sync back to parent
  useEffect(() => {
    if (!isInternalUpdate.current) return;
    isInternalUpdate.current = false;

    if (panes.length > 0) {
      const allTabs = panes.flatMap((pane) => pane.tabs);
      const activePaneTab =
        panes.find((pane) => pane.activeTabId)?.activeTabId || "";

      const currentTabIds = tabs
        .map((t) => t.id)
        .sort()
        .join(",");
      const newTabIds = allTabs
        .map((t) => t.id)
        .sort()
        .join(",");

      if (currentTabIds !== newTabIds || activeTabId !== activePaneTab) {
        onTabsUpdate(allTabs, activePaneTab);
      }
    } else if (tabs.length > 0) {
      onTabsUpdate([], "");
    }
    // Added missing dependencies tabs, activeTabId, onTabsUpdate
  }, [panes, tabs, activeTabId, onTabsUpdate]);

  const handleSplitPane = (paneId: string) => {
    const newPane: Pane = {
      id: `pane-${Date.now()}`,
      tabs: [],
      activeTabId: "",
    };

    isInternalUpdate.current = true;

    setPanes((prevPanes) => {
      const paneIndex = prevPanes.findIndex((p) => p.id === paneId);
      /* v8 ignore next -- defensive: split is only ever invoked from an existing pane */
      if (paneIndex === -1) return prevPanes;

      const newPanes = [...prevPanes];
      newPanes.splice(paneIndex + 1, 0, newPane);
      return newPanes;
    });
  };

  // NEW: Handler to close a specific pane
  const handleClosePane = (paneId: string) => {
    isInternalUpdate.current = true;
    setPanes((prevPanes) => prevPanes.filter((p) => p.id !== paneId));
  };
  const handleTabClose = (paneId: string, tabId: string) => {
    isInternalUpdate.current = true;

    setPanes((prevPanes) => {
      const newPanes: Pane[] = [];
      for (const pane of prevPanes) {
        if (pane.id !== paneId) {
          newPanes.push(pane);
          continue;
        }

        const updatedTabs = pane.tabs.filter((tab) => tab.id !== tabId);
        let newActiveTabId = pane.activeTabId;

        if (updatedTabs.length === 0) {
          newActiveTabId = "";
        } else if (pane.activeTabId === tabId) {
          newActiveTabId = updatedTabs[updatedTabs.length - 1].id;
        }

        if (updatedTabs.length > 0) {
          newPanes.push({
            ...pane,
            tabs: updatedTabs,
            activeTabId: newActiveTabId,
          });
        }
      }
      return newPanes;
    });
  };

  const handleTabClick = (paneId: string, tabId: string) => {
    isInternalUpdate.current = true;

    setPanes((prevPanes) => {
      const newPanes: Pane[] = [];
      for (const pane of prevPanes) {
        if (pane.id === paneId) {
          newPanes.push({ ...pane, activeTabId: tabId });
        } else {
          newPanes.push(pane);
        }
      }
      return newPanes;
    });
  };

  const handleMoveTab = (
    tabId: string,
    fromPaneId: string,
    toPaneId: string,
  ) => {
    isInternalUpdate.current = true;

    setPanes((prevPanes) => {
      const newPanes = [...prevPanes];
      const fromPane = newPanes.find((p) => p.id === fromPaneId);
      const tabToMove = fromPane?.tabs.find((t) => t.id === tabId);

      if (!fromPane || !tabToMove) return prevPanes;

      fromPane.tabs = fromPane.tabs.filter((t) => t.id !== tabId);

      if (fromPane.activeTabId === tabId && fromPane.tabs.length > 0) {
        fromPane.activeTabId = fromPane.tabs[fromPane.tabs.length - 1].id;
      } else if (fromPane.tabs.length === 0) {
        fromPane.activeTabId = "";
      }
      const toPane = newPanes.find((p) => p.id === toPaneId);
      if (toPane) {
        toPane.tabs = [...toPane.tabs, tabToMove];
        toPane.activeTabId = tabToMove.id;
      }

      return newPanes.filter((pane) => pane.tabs.length > 0);
    });
  };

  if (panes.length === 0) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-row gap-4 overflow-x-auto h-full">
        {panes.map((pane) => (
          <div key={pane.id} className="flex-1 min-w-[300px]">
            <PaneContainer
              pane={pane}
              onTabClose={handleTabClose}
              onTabClick={handleTabClick}
              onMoveTab={handleMoveTab}
              onSplit={handleSplitPane}
              onClosePane={handleClosePane} // Pass the handler down
              showSplit={panes.length < 3}
              globalDateRange={globalDateRange}
              measureDataMap={measureDataMap}
            />
          </div>
        ))}
      </div>
    </DndProvider>
  );
}
